import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, Clock, Broom, PencilSimple } from '@phosphor-icons/react';
import { useAuth } from '../hooks/useAuth';
import { useVisitAllocation } from '../hooks/useVisitAllocation';
import WorkerAllocation from '../components/WorkerAllocation';
import './ProviderDashboard.css';

const ProviderDashboard = () => {
  const { activeUserEmail, activeUserRoles, activeEmployeeId, loading: authLoading } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const allocation = useVisitAllocation((visitId, assignment) => {
    setDashboardData((current) => {
      if (!current) return current;
      const updateVisit = (item) => String(item.id) === String(visitId)
        ? { ...item, workerId: assignment.workerId, worker: assignment.worker, action: assignment.workerId ? 'Open' : 'Allocate' }
        : item;
      return {
        ...current,
        todaysServices: (current.todaysServices || []).map(updateVisit),
        allServices: (current.allServices || []).map(updateVisit),
      };
    });
  });
  const [loading, setLoading] = useState(true);
  const activeRoleHeader = (activeUserRoles || []).join(',');

  useEffect(() => {
    if (authLoading) return;

    let isMounted = true;
    const controller = new AbortController();

    const loadDashboard = async () => {
      try {
        setLoading(true);
        const res = await fetch(`/api/dashboard?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
          signal: controller.signal,
          headers: {
            'x-kcs-user-email': activeUserEmail || '',
            'x-kcs-user-roles': activeRoleHeader,
            'x-kcs-provider-employee-id': activeEmployeeId || '167',
          },
        });

        if (!res.ok) {
          throw new Error(`Dashboard API unavailable: ${res.status}`);
        }

        const data = await res.json();
        if (isMounted) {
          setDashboardData(data);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading dashboard:', err);
          if (isMounted) {
            setDashboardData(null);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [authLoading, activeUserEmail, activeRoleHeader, activeEmployeeId]);

  const renderWorkerAllocation = (item) => (
    <WorkerAllocation item={item} {...allocation} saving={allocation.savingAssignments[item.id]} error={allocation.assignmentErrors[item.id]} />
  );

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!dashboardData) {
    return <div className="error">Failed to load dashboard data</div>;
  }

  const metrics = dashboardData?.metrics || {};
  const todaysServices = dashboardData?.todaysServices || [];
  const canManageServices = (activeUserRoles || []).some((role) => ['admin', 'kcs-super-user'].includes((role || '').toLowerCase()));

  const metricCards = [
    { title: 'Due Today', count: metrics.dueToday || 0, color: 'blue', icon: Clock, link: '/provider/schedule?filter=today' },
    { title: 'Upcoming (7 days)', count: metrics.upcomingSevenDays || 0, color: 'green', icon: Clock, link: '/provider/schedule?filter=upcoming' },
    ...(canManageServices ? [{ title: 'Unallocated Services', count: metrics.unallocatedServices || 0, color: 'orange', icon: Users, link: '/provider/services?filter=unallocated' }] : []),
    { title: 'Overdue / Missed', count: metrics.overdueMissed || 0, color: 'purple', icon: Clock, link: '/provider/schedule?filter=overdue' },
    { title: 'Incident', count: metrics.awaitingReview || 0, color: 'red', icon: Clock, link: '/provider/schedule?filter=incident' },
  ];

  const getStatusClass = (status) => {
    const statusMap = {
      'Due': 'status-due',
      'Overdue': 'status-overdue',
      'Completed': 'status-completed',
      'Done': 'status-completed',
      'Not done': 'status-overdue',
      'Cancelled': 'status-default',
      'Confirmed': 'status-confirmed',
    };
    return statusMap[status] || 'status-default';
  };

  return (
    <div className="provider-dashboard">
      <div className="page-header">
        <h1>Dashboard</h1>
      </div>
      {allocation.workersError && <div role="alert">{allocation.workersError}</div>}

      <div className="metrics-grid">
        {metricCards.map((metric, index) => {
          const IconComponent = metric.icon;
          const cardContent = (
            <>
              <div className="metric-icon">
                <IconComponent size={40} weight="regular" />
              </div>
              <div className="metric-content">
                <div className="metric-count">{metric.count}</div>
                <h3 className="metric-title">{metric.title}</h3>
              </div>
            </>
          );

          if (metric.link) {
            return (
              <Link
                key={index}
                to={metric.link}
                className={`metric-card metric-${metric.color} metric-card-link`}
              >
                {cardContent}
              </Link>
            );
          }

          return (
            <div key={index} className={`metric-card metric-${metric.color}`}>
              {cardContent}
            </div>
          );
        })}
      </div>

      <div className="table-section">
        <div className="section-header">
          <h2 className="section-title">Scheduled Visits</h2>
          <Link to="/provider/schedule" className="view-all-link"><Clock size={16} weight="regular" /> View all</Link>
        </div>

        {/* Mobile Card View */}
        <div className="mobile-cards">
          {todaysServices && todaysServices.length > 0 ? (
            todaysServices.map((service, index) => {
              return (
                <div key={index} className="service-card">
                  <div className="card-header">
                    <h3 className="card-customer">{service.customer}</h3>
                    <span className={`status-badge ${getStatusClass(service.status)}`}>
                      {service.status}
                    </span>
                  </div>
                  <div className="card-details-compact">
                    <div className="card-info-item">
                      <Calendar size={16} weight="regular" className="card-icon" />
                      <span><strong>{service.startDateDisplay || service.scheduledDate}</strong></span>
                    </div>
                    <div className="card-info-item">
                      <Clock size={16} weight="regular" className="card-icon" />
                      <span>{service.time}</span>
                    </div>
                    <div className="card-info-item">
                      <Broom size={16} weight="regular" className="card-icon" />
                      <span>{service.service}</span>
                    </div>
                    <div className="card-info-item">
                      <Users size={16} weight="regular" className="card-icon" />
                      {renderWorkerAllocation(service)}
                    </div>
                  </div>
                  <div className="card-actions single-action">
                    <Link to={`/provider/services/${service.id}/edit-job`} className="action-button btn-icon" title="Edit job">
                      <PencilSimple size={24} weight="regular" />
                    </Link>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="empty-state">No scheduled visits found.</p>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Start Date</th>
                <th>Time</th>
                <th>Customer</th>
                <th>Service</th>
                <th>Worker</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {todaysServices && todaysServices.length > 0 ? (
                todaysServices.map((service) => {
                  return (
                    <tr key={service.id}>
                      <td><strong>{service.startDateDisplay || service.scheduledDate}</strong></td>
                      <td>{service.time}</td>
                      <td><strong>{service.customer}</strong></td>
                      <td>{service.service}</td>
                      <td>
                        {renderWorkerAllocation(service)}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusClass(service.status)}`}>
                          {service.status}
                        </span>
                      </td>
                      <td>
                        <Link 
                          to={`/provider/services/${service.id}/edit-job`}
                          className="action-button btn-icon"
                          title="Edit job"
                        >
                          <PencilSimple size={24} weight="regular" />
                        </Link>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    No scheduled visits found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProviderDashboard;
